document.documentElement.classList.remove('no-js');

class GuanyiTheme {
  constructor() {
    this.previousMenuFocus = null;
    this.init(document);
  }

  init(root) {
    this.initMobileMenu(root);
    this.initDisclosures(root);
    this.initQuantity(root);
    this.initProductGallery(root);
    this.initOptionSelectors(root);
    this.initAccordions(root);
    this.initContactPrefill(root);
    this.initCollectionControls(root);
    this.initLocalization(root);

    this.initHeaderScroll();
  }

  bindOnce(element, key = 'guanyiBound') {
    if (!element || element.dataset[key] === 'true') return false;
    element.dataset[key] = 'true';
    return true;
  }

  getFocusable(container) {
    return Array.from(container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter((element) => !element.hidden && element.offsetParent !== null);
  }

  initMobileMenu(root) {
    const toggle = root.querySelector?.('[data-mobile-menu-toggle]') || document.querySelector('[data-mobile-menu-toggle]');
    const panel = root.querySelector?.('[data-mobile-menu]') || document.querySelector('[data-mobile-menu]');
    if (!toggle || !panel || !this.bindOnce(panel, 'menuBound')) return;

    const dialog = panel.querySelector('.mobile-nav__panel');
    const closeButtons = panel.querySelectorAll('[data-mobile-menu-close]');

    const setOpen = (open) => {
      panel.classList.toggle('is-open', open);
      document.body.classList.toggle('menu-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      panel.setAttribute('aria-hidden', String(!open));
      panel.inert = !open;
      if (open) {
        this.previousMenuFocus = document.activeElement;
        window.requestAnimationFrame(() => {
          (panel.querySelector('[data-mobile-menu-close]') || dialog)?.focus();
        });
      } else if (this.previousMenuFocus instanceof HTMLElement) {
        this.previousMenuFocus.focus();
      }
    };

    toggle.addEventListener('click', () => setOpen(!panel.classList.contains('is-open')));
    closeButtons.forEach((button) => button.addEventListener('click', () => setOpen(false)));
    panel.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        return;
      }
      if (event.key !== 'Tab' || !panel.classList.contains('is-open')) return;
      const focusable = this.getFocusable(dialog);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  initDisclosures(root) {
    root.querySelectorAll?.('[data-disclosure-toggle]').forEach((button) => {
      if (!this.bindOnce(button, 'disclosureBound')) return;
      const target = document.getElementById(button.getAttribute('aria-controls'));
      if (!target) return;

      const close = (restoreFocus = false) => {
        button.setAttribute('aria-expanded', 'false');
        target.hidden = true;
        if (restoreFocus) button.focus();
      };

      button.addEventListener('click', () => {
        const isOpen = button.getAttribute('aria-expanded') === 'true';
        if (isOpen) {
          close(false);
        } else {
          button.setAttribute('aria-expanded', 'true');
          target.hidden = false;
          window.requestAnimationFrame(() => target.querySelector('input, select, button, a')?.focus());
        }
      });

      document.addEventListener('click', (event) => {
        if (target.hidden || target.contains(event.target) || button.contains(event.target)) return;
        close(false);
      });

      target.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          close(true);
        }
      });
    });
  }

  initQuantity(root) {
    root.querySelectorAll?.('[data-quantity-wrapper]').forEach((wrapper) => {
      if (!this.bindOnce(wrapper, 'quantityBound')) return;
      const input = wrapper.querySelector('input[type="number"]');
      if (!input) return;
      const step = () => Number(input.step || 1);
      wrapper.querySelector('[data-quantity-minus]')?.addEventListener('click', () => {
        const min = Number(input.min || 1);
        const next = Number(input.value || min) - step();
        input.value = String(Math.max(min, next));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      wrapper.querySelector('[data-quantity-plus]')?.addEventListener('click', () => {
        const max = input.max ? Number(input.max) : Infinity;
        const next = Number(input.value || 0) + step();
        input.value = String(Math.min(max, next));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      input.addEventListener('change', () => {
        const min = Number(input.min || 1);
        const max = input.max ? Number(input.max) : Infinity;
        const increment = step();
        let value = Number(input.value || min);
        value = Math.min(max, Math.max(min, value));
        value = min + Math.round((value - min) / increment) * increment;
        input.value = String(Math.min(max, Math.max(min, value)));
      });
    });
  }

  initProductGallery(root) {
    root.querySelectorAll?.('[data-product-gallery]').forEach((gallery) => {
      if (!this.bindOnce(gallery, 'galleryBound')) return;
      const mediaItems = Array.from(gallery.querySelectorAll('[data-gallery-media]'));
      const thumbs = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const targetId = thumb.dataset.mediaId;
          mediaItems.forEach((media) => {
            const active = String(media.dataset.mediaId) === String(targetId);
            if (!active) {
              media.querySelectorAll('video').forEach((video) => video.pause());
            }
            media.hidden = !active;
            media.classList.toggle('is-active', active);
          });
          thumbs.forEach((item) => {
            const active = item === thumb;
            item.classList.toggle('is-active', active);
            item.setAttribute('aria-pressed', String(active));
          });
        });
      });
    });
  }

  initOptionSelectors(root) {
    root.querySelectorAll?.('[data-option-selectors]').forEach((container) => {
      if (!this.bindOnce(container, 'optionsBound')) return;
      container.querySelectorAll('[data-option-select]').forEach((select) => {
        select.addEventListener('change', () => {
          const productSection = select.closest('[data-product-section]');
          if (!productSection) return;
          const selectedOption = select.options[select.selectedIndex];
          const basePath = selectedOption?.dataset.productUrl || productSection.dataset.productUrl || window.location.pathname;
          const selectedIds = Array.from(container.querySelectorAll('[data-option-select]'))
            .map((field) => field.options[field.selectedIndex]?.dataset.optionValueId)
            .filter(Boolean);
          const url = new URL(basePath, window.location.origin);
          if (selectedIds.length) url.searchParams.set('option_values', selectedIds.join(','));
          url.searchParams.delete('variant');
          window.location.assign(url.toString());
        });
      });
    });
  }

  initCollectionControls(root) {
    root.querySelectorAll?.('[data-sort-select]').forEach((select) => {
      if (!this.bindOnce(select, 'sortBound')) return;
      select.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        window.location.assign(url.toString());
      });
    });

    root.querySelectorAll?.('[data-facet-toggle]').forEach((toggle) => {
      if (!this.bindOnce(toggle, 'facetBound')) return;
      const target = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!target) return;
      toggle.addEventListener('click', () => {
        const open = target.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', String(open));
        if (open) target.querySelector('input, summary, button')?.focus();
      });
    });
  }

  initContactPrefill(root) {
    const field = root.querySelector?.('[data-contact-product]');
    if (!field || !this.bindOnce(field, 'prefillBound')) return;
    const params = new URLSearchParams(window.location.search);
    const values = [params.get('product'), params.get('sku'), params.get('options')].filter(Boolean);
    if (values.length) field.value = values.join(' / ');
  }

  initAccordions(root) {
    root.querySelectorAll?.('[data-accordion-button]').forEach((button) => {
      if (!this.bindOnce(button, 'accordionBound')) return;
      button.addEventListener('click', () => {
        const panel = document.getElementById(button.getAttribute('aria-controls'));
        if (!panel) return;
        const expanded = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!expanded));
        panel.hidden = expanded;
      });
    });
  }


  initHeaderScroll() {
    const header = document.querySelector('.site-header');
    if (!header || header.dataset.scrollBound === 'true') return;
    header.dataset.scrollBound = 'true';
    const update = () => header.classList.toggle('is-scrolled', window.scrollY > 18);
    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  initLocalization(root) {
    root.querySelectorAll?.('[data-localization-select]').forEach((select) => {
      if (!this.bindOnce(select, 'localizationBound')) return;
      select.addEventListener('change', () => select.form?.submit());
    });
  }
}

const guanyiTheme = new GuanyiTheme();
document.addEventListener('shopify:section:load', (event) => guanyiTheme.init(event.target));
